import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, FormControl, InputLabel, Select, MenuItem, TextField, Button, CircularProgress, LinearProgress, Card, CardContent, Chip
} from '@mui/material';
import { Search as SearchIcon, EmojiEvents as EmojiEventsIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import MainLayout from '../layouts/MainLayout';
import { colaboradorService, Colaborador } from '../services/colaboradorService';
import { MesCompetencia } from '../services/metaColaboradorService';
import { formatarMoeda } from '../utils/formatters';

// Mock de dados para o desempenho
const mockDesempenho = [
  { ramoId: 1, ramoNome: 'Automóvel', valorMeta: 15000, valorAtingido: 12500 },
  { ramoId: 2, ramoNome: 'Residencial', valorMeta: 8000, valorAtingido: 9200 },
  { ramoId: 3, ramoNome: 'Vida', valorMeta: 20000, valorAtingido: 10000 },
  { ramoId: 4, ramoNome: 'Empresarial', valorMeta: 25000, valorAtingido: 12000 },
];

const DesempenhoPage: React.FC = () => {
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [loading, setLoading] = useState(false);
  const [buscou, setBuscou] = useState(false);

  // Filtros
  const [filtroColaborador, setFiltroColaborador] = useState<number | ''>('');
  const [filtroAno, setFiltroAno] = useState<string>(new Date().getFullYear().toString());
  const [filtroMes, setFiltroMes] = useState<MesCompetencia>((Object.keys(MesCompetencia)[new Date().getMonth()]) as MesCompetencia);

  useEffect(() => {
    // Carrega os colaboradores para o dropdown do filtro
    const carregarColaboradores = async () => {
      try {
        const data = await colaboradorService.listar();
        setColaboradores(data);
      } catch (error) {
        toast.error('Erro ao carregar colaboradores.');
      }
    };
    carregarColaboradores();
  }, []);

  const handleAnalisar = () => {
    if (!filtroColaborador) {
      toast.warn('Por favor, selecione um colaborador para analisar.');
      return;
    }
    setLoading(true);
    setBuscou(false); // Reseta a busca anterior
    // Simula uma chamada de API
    setTimeout(() => {
      setBuscou(true);
      setLoading(false);
    }, 1000);
  };

  const getProgressColor = (progresso: number): 'success' | 'warning' | 'primary' => {
    if (progresso >= 100) return 'success';
    if (progresso >= 50) return 'warning';
    return 'primary';
  };

  return (
    <MainLayout>
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" color="primary.main" sx={{ fontWeight: 'bold', mb: 4 }}>
          Análise de Desempenho
        </Typography>

        {/* Seção de Filtros */}
        <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Filtros de Análise</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Colaborador</InputLabel>
                <Select value={filtroColaborador} label="Colaborador" onChange={(e) => setFiltroColaborador(e.target.value as number)}>
                  {colaboradores.map((c) => (<MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth variant="outlined" label="Ano" value={filtroAno} onChange={(e) => setFiltroAno(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Mês</InputLabel>
                <Select value={filtroMes} label="Mês" onChange={(e) => setFiltroMes(e.target.value as MesCompetencia)}>
                  {Object.values(MesCompetencia).map((mes) => (<MenuItem key={mes} value={mes}>{mes}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button fullWidth variant="contained" startIcon={<SearchIcon />} onClick={handleAnalisar} sx={{ height: '56px' }}>
                Analisar
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Seção de Resultados */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : buscou && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Resultados</Typography>
            <Grid container spacing={3}>
              {mockDesempenho.map((item) => {
                const progresso = item.valorMeta > 0 ? (item.valorAtingido / item.valorMeta) * 100 : 0;
                const progressoLimitado = Math.min(progresso, 100);

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.ramoId}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderTop: `4px solid ${(theme:any) => theme.palette[getProgressColor(progresso)].main}` }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="div" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                          {item.ramoNome}
                        </Typography>
                        <Typography color="text.secondary" gutterBottom>
                          Meta: {formatarMoeda(item.valorMeta)}
                        </Typography>
                        
                        <Box sx={{ my: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Progresso</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{progresso.toFixed(1)}%</Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={progressoLimitado}
                            color={getProgressColor(progresso)}
                            sx={{ height: 10, borderRadius: 5 }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                          <Typography variant="body1">
                            Atingido:
                          </Typography>
                          <Typography variant="h6" color={getProgressColor(progresso) + '.dark'}>
                            {formatarMoeda(item.valorAtingido)}
                          </Typography>
                        </Box>

                        {progresso >= 100 && (
                           <Chip icon={<EmojiEventsIcon />} label="Meta Batida!" color="success" sx={{ mt: 2, fontWeight: 'bold' }} />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}
      </Box>
    </MainLayout>
  );
};

export default DesempenhoPage;
